import { array, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { SafeString } from '@ember/template';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';
import { stripHtml } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TeamsMembersListComponent from 'webapp/components/teams/members-list';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Team from 'webapp/models-custom/team';

interface Args {
	model: Team;
}

export type Action = 'openActivityLog' | 'delete' | 'leave';
export type Option = PopupListItem<Action>;

export default class TeamsIndexGridItemComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked additionalOptionsOpened = false;
	@tracked isVisibleOnScreen = false;

	get model(): Team {
		return this.args.model;
	}

	get isPrivate(): boolean {
		return this.model.isPrivate;
	}

	get isClosed(): boolean {
		return this.model.isClosed;
	}

	get isOpen(): boolean {
		return this.model.isOpen;
	}

	get backgroundColor(): SafeString {
		return htmlSafe(`background-color: ${this.model.color}`);
	}

	get backgroundColorWithOpacity(): SafeString {
		const colorWithOpacity = `${this.model.color}50`;
		return htmlSafe(`background-color: ${colorWithOpacity}`);
	}

	get isUserMember(): boolean {
		return this.model.isUserMember;
	}

	get displayJoin(): boolean {
		return !this.isUserMember && this.isOpen;
	}

	get displayRequestToJoin(): boolean {
		return !this.isUserMember && this.isClosed;
	}

	get displayRequested(): boolean {
		return this.model.hasUserSentRequest;
	}

	get strippedDescription(): string {
		return stripHtml(this.model.description ?? '');
	}

	get options(): Option[] {
		const options: Option[] = [];

		const model = this.model;

		if (this.currentUserService.isAdmin) {
			options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
		}

		if (!(model.isOwnedByMe && model.teamMembers?.length === 1)) {
			options.push({
				icon: 'Logout',
				text: 'Leave space',
				action: 'leave',
			});
		}

		if (model.isOwnedByMe) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		return options;
	}

	get userStatusText() {
		return this.model.isOwnedByMe ? 'Owner' : 'Member';
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	navigateToTeamDetailsPage(event: MouseEvent | TouchEvent): void {
		event.preventDefault();
		this.navigationService.transitionToRoute('teams.view', this.model.id);
	}

	@action
	openEditTeamPopup(): void {
		this.mmTeamsService.openEditPopup(this.model);
	}

	@action
	joinTeam(): void {
		this.mmTeamsService.joinTeam(this.model);
	}

	@action
	requestToJoin(): void {
		this.mmTeamsService.requestToJoin(this.model);
	}

	@action
	openAdditionalOptions(e: PointerEvent): void {
		if (this.additionalOptionsOpened) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.context');
		const popup = this.popupService.createListPopup(attachTo, undefined, 'team-options');

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.additionalOptionsOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.additionalOptionsOpened = false;
				}
			});
		}
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openActivityLog(): void {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.args.model,
		});
	}

	@action
	delete(): void {
		this.mmTeamsService.handleDelete(this.model);
	}

	@action
	leave(): void {
		this.mmTeamsService.handleLeave(this.model);
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					@model.animationClass
					"card"
					"card--preview"
					"flex-item"
					"filter-sort-item"
					"card-team"
					(if this.additionalOptionsOpened "card--show-overlay")
				)
			}}
		>
			<div class="preview">
				<div class="preview-content" style={{this.backgroundColorWithOpacity}}>
					<span style={{this.backgroundColor}} class="preview-content--avatar">
						<CommonUiIconComponent @name="Spaces" @size="xl" />
					</span>
				</div>
			</div>

			{{#if this.isVisibleOnScreen}}
				<div role="button"></div>
				<div class="overlay">
					<div class="overlay-bg"></div>

					<div class="wrap">
						<div class="actions">
							{{#if this.displayRequested}}
								<div class="actions__top">
									<CommonUiIconComponent @name="Clock" @size="sm" />
									Requested
								</div>
							{{else if this.isUserMember}}
								<div class="actions__top">
									<SubmitButtonComponent
										@text="View"
										@extraClass="presentbutton"
										@onTrigger={{this.navigateToTeamDetailsPage}}
										@noDelay={{true}}
									/>
								</div>
								<div class="actions__bottom">
									<SubmitButtonComponent
										@text="Edit"
										@extraClass="editbutton"
										@onTrigger={{this.openEditTeamPopup}}
										@noDelay={{true}}
										@icon="Pencil"
										@theme="grey-outlined"
									/>
								</div>
							{{else if this.displayRequestToJoin}}
								<div class="actions__top">
									<SubmitButtonComponent
										@text="Request to join"
										@extraClass="presentbutton"
										@onTrigger={{this.requestToJoin}}
										@noDelay={{true}}
									/>
								</div>
							{{else if this.displayJoin}}
								<div class="actions__top">
									<SubmitButtonComponent
										@text="Join"
										@extraClass="presentbutton"
										@onTrigger={{this.joinTeam}}
										@noDelay={{true}}
									/>
								</div>
								<div class="actions__bottom">
									<SubmitButtonComponent
										@text="View"
										@extraClass="editbutton"
										@onTrigger={{this.navigateToTeamDetailsPage}}
										@noDelay={{true}}
										@theme="blue-outlined"
									/>
								</div>
							{{/if}}
						</div>
					</div>
				</div>

				{{#if this.isUserMember}}
					<div class="top">
						<div class="top__left">
							<span class="member-label">{{this.userStatusText}}</span>
						</div>
						<div class="top__right">
							<div
								role="button"
								{{on "click" this.openAdditionalOptions}}
								class="context"
							>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						</div>
					</div>
				{{/if}}

				<div class="info">
					<div class="anti-stretch">
						<div class="name">
							<div class="text-wrapper">{{this.model.name}}</div>
							{{#unless this.isOpen}}
								<div class="icons-wrapper">
									{{#if this.isClosed}}
										<ToolTipWithIconComponent
											@iconName="Lock"
											@text="This is a closed space"
										/>
									{{/if}}

									{{#if this.isPrivate}}
										<ToolTipWithIconComponent
											@iconName="EyeClosed"
											@text="This is a private space"
										/>
									{{/if}}
								</div>
							{{/unless}}
						</div>
						<EditableModelPropertyComponent
							@model={{hash description=this.strippedDescription}}
							@canEdit={{false}}
							@editableProperty="description"
							@className="description"
						/>
					</div>
				</div>
				<div class="bottom">
					<TeamsMembersListComponent
						@model={{@model}}
						@hidePendingBadge={{true}}
						@isPreview={{true}}
					/>
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged false}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}
		</li>
	</template>
}
